import { Directive, ElementRef, inject, Input, OnDestroy, OnInit, Renderer2, RendererStyleFlags2 } from '@angular/core';
import { IListingSetup, Listing } from '@df/catalog-ui/listing/listing';
import { IProductListingConfig, ProductListing } from '@df/catalog-ui/listing/product-listing';
import { Model } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { Carousel } from '@df/ui-carousel/carousel.interface';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { merge } from 'rxjs/internal/observable/merge';
import { Subscription } from 'rxjs/internal/Subscription';
import { Swiper } from 'swiper';
import { ProductListingFactoryDecorator } from '../../../listing/product-listing.factory.decorator';
import { InternalProductsService } from '../../../product/internal-products.service';
import { IModelCarouselData, IModelCarouselLoadRequest } from './model-carousel.interface';
import { ModelCarouselService } from './model-carousel.service';

@Directive()
export abstract class ModelCarouselComponentDef<T extends Model, D extends IModelCarouselData = IModelCarouselData>
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit, OnDestroy
{
    @Input()
    listId = 'products-carousel';

    @Input()
    listName = 'Products Carousel';

    @ICmsContent({
        type: 'component'
    })
    @Input()
    component!: D;

    abstract type: string;

    listing?: Listing | ProductListing;
    touched = false;
    loop = false;
    swiper!: Swiper;
    protected listingSubscription?: Subscription;

    protected modelCarouselService = inject(ModelCarouselService);
    protected internalProductsService = inject(InternalProductsService);
    protected renderer = inject(Renderer2);
    protected productListingFactory = inject(ProductListingFactoryDecorator);
    protected app = inject(App);
    protected iCmsDataChangeService = inject(ICmsDataChangeService);
    readonly elementRef = inject(ElementRef);

    override ngOnInit(): void {
        super.ngOnInit();

        this.setModels();

        this.subscriptions.add(
            this.iCmsDataChangeService.afterChange$.subscribe(event => {
                if (
                    event.changeType === NICmsDataChange.EChangeType.change &&
                    this.cmsObservedData.some(path => event.valuePath.includes(path))
                ) {
                    this.setModels();

                    this.detectChanges();
                }
            })
        );

        if (this.type === Product.type) {
            this.subscriptions.add(
                merge(this.client.obs.breakpoint, this.internalProductsService.update).subscribe(() => {
                    const currentLength = this.models?.length;
                    this.onModelsSet();

                    if (currentLength !== this.models?.length) {
                        this.detectChanges();
                    }
                })
            );
        }
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this.listing) {
            this.listing.clearSubscriptions();
        }

        this.listingSubscription?.unsubscribe();
    }

    get cmsObservedData(): string[] {
        return ['data', 'variant', 'category_id', 'limit', 'ids', 'items', 'id', 'product_view', 'product_label'];
    }

    get models(): T[] {
        return (this.listing?.models as T[]) || [];
    }

    set models(models: T[]) {
        if (this.listing) {
            (this.listing as Listing<T>).setSourceModels(models);
        }
    }

    get gtmTrackViewItemList(): Gtm.ITrackViewItemList<T> {
        return {
            listId: this.component.id || this.listId,
            listName: this.component.title || this.listName,
            models: this.models
        };
    }

    get canLoop(): boolean {
        return !!this.swiper?.allowSlideNext && !!this.swiper?.allowSlidePrev && this.loop;
    }

    /**
     * Checks if the carousel can be dragged/scrolled, even without loop enabled.
     * This is used to determine if the custom cursor should be shown.
     */
    get canDrag(): boolean {
        if (!this.swiper?.wrapperEl) {
            return false;
        }

        const wrapper = this.swiper.wrapperEl;
        const wrapperWidth = wrapper.offsetWidth;
        const slidesWidth = wrapper
            .querySelector('slot')
            ?.assignedElements()
            .map(el => (<HTMLElement>el).offsetWidth)
            .reduce((total, width) => total + width, 0) || 0;

        // Carousel can be dragged if slides width exceeds wrapper width
        return slidesWidth > wrapperWidth;
    }

    afterInitHandle(eventParams: Carousel.EventsParams['afterInit']) {
        const [swiper] = eventParams;
        this.swiper = swiper;

        this.renderer.setStyle(this.elementRef.nativeElement, '--carousel-width', swiper.width, RendererStyleFlags2.DashCase);
        this.renderer.addClass(this.elementRef.nativeElement, 'is-initialized');

        this.shouldCarouselBeEnabled();
    }

    activeIndexChangeHandle(eventParams: Carousel.EventsParams['activeIndexChange']) {
    }

    touchStartHandle() {
        if (!this.touched) {
            this.touched = true;
            this.detectChanges();
        }
    }

    touchMoveHandle(eventParams: Carousel.EventsParams['touchMove']) {
        this.touchStartHandle();
    }

    trackById(index: number, item: Model) {
        return item.id;
    }

    /**
     * We can use full path or path with wildcard `carousel*` to observe all carousel fields
     */
    protected cmsFieldPathChanged(valuePath: string[]): boolean {
        return this.cmsObservedData.some(path => !!valuePath.find(p => (p.endsWith('*') ? p.startsWith(path.slice(-1)) : p === path)));
    }

    protected onModelsSet(): void {}

    /**
     * Ger models request
     */
    protected getLoadRequest(): IModelCarouselLoadRequest {
        return this.component;
    }

    /**
     * Models updater
     */
    protected setModels() {
        const data = { ...this.getLoadRequest() };
        const limit = data.limit;

        if (limit && this.type === 'product') {
            // load 2 times more in case of stock check
            data.limit = data.limit * 2;
        }

        const state = this.modelCarouselService.getUid(this.type, data);
        const currentState = state;

        this.modelCarouselService.loadModels(this.type, data).then(models => {
            if (state === currentState) {
                if (this.listing) {
                    this.listing.setSourceModels(models).applyFilters();
                } else {
                    const options = this.getListingOptions();

                    this.listing =
                        this.type === 'product'
                            ? this.productListingFactory.create(options, models, this.getProductListingSetup())
                            : new Listing(options, models);
                    this.listingSubscription = this.listing.subscribe(() => {
                        this.detectChanges();
                    });
                }

                this.listing.setLimit(limit);

                this.onModelsSet();

                this.detectChanges();
            }
        });
    }

    protected getListingOptions(): IProductListingConfig {
        return { type: this.type, stockCheck: true };
    }

    protected getProductListingSetup(): IListingSetup<Product<IProductDataV2>> | undefined {
        return undefined;
    }

    protected shouldCarouselBeEnabled(): void {
        const wrapper = this.swiper?.wrapperEl;

        if (!wrapper || !this.swiper?.params) {
            return;
        }

        const wrapperWidth = wrapper.offsetWidth;
        const slidesWidth = wrapper
            .querySelector('slot')
            ?.assignedElements()
            .map(el => (<HTMLElement>el).offsetWidth)
            .reduce((total, width) => total + width, 0) || 0;

        if (slidesWidth <= wrapperWidth) {
            // All slides fit in viewport - disable loop and swiper
            this.swiper.loopDestroy();
            this.swiper.disable();
            this.loop = false;
        } else {
            // Slides exceed viewport - enable swiper for dragging
            // Swiper must be enabled to allow freeMode dragging even without loop
            this.swiper.enable();
            
            // Set loop to true if slides exceed viewport
            // This allows canLoop getter to check component.carousel_loop
            this.loop = true;
            
            // Update loop state based on canLoop getter
            // canLoop checks both this.loop and component.carousel_loop
            // Loop is only created when both conditions are met
            if (this.canLoop) {
                this.swiper.loopCreate();
            } else {
                this.swiper.loopDestroy();
            }
        }

        this.swiper.update();

        this.detectChanges();
    }
}
